/**
 * EmbeddedContext — Audio Agent runs in two shells:
 *
 *   1. Standalone (its own tab / domain) — full app: Upload, History,
 *      Sign in/out, Results pages.
 *
 *   2. Embedded — iframed inside knkt-local's Tools tab. The host
 *      already provides KNKT chrome (top nav, sign-out, user menu),
 *      so we strip our own redundant chrome and surface a compact
 *      subset: Upload + the current analysis. History + Sign-out are
 *      hidden — a clear "Open full Audio Agent ↗" pill lets the user
 *      break out to the standalone version for History + full surface.
 *
 * Detection: presence of `?embedded=1` in the URL (set by knkt-local's
 * ToolsContainer when building the iframe URL). Persisted to
 * `sessionStorage` so internal navigation doesn't lose it.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface EmbeddedContextValue {
  embedded: boolean;
  /** Public URL of the standalone Audio Agent app — used by "Open full" */
  standaloneUrl: string;
}

const EmbeddedContext = createContext<EmbeddedContextValue>({
  embedded: false,
  standaloneUrl: typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '',
});

const KEY = 'knkt-aa-embedded';

export const EmbeddedProvider = ({ children }: { children: ReactNode }) => {
  const [embedded, setEmbedded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (sessionStorage.getItem(KEY) === '1') return true;
    const p = new URLSearchParams(window.location.search);
    return p.get('embedded') === '1' || p.get('embed') === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    const flag = p.get('embedded') === '1' || p.get('embed') === '1';
    if (flag) {
      sessionStorage.setItem(KEY, '1');
      setEmbedded(true);
      p.delete('embedded');
      p.delete('embed');
      const search = p.toString();
      const next = window.location.pathname + (search ? `?${search}` : '');
      window.history.replaceState({}, '', next);
    }
  }, []);

  const standaloneUrl =
    typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : '';

  return (
    <EmbeddedContext.Provider value={{ embedded, standaloneUrl }}>
      {children}
    </EmbeddedContext.Provider>
  );
};

export const useEmbeddedMode = () => useContext(EmbeddedContext);
